import * as React from 'react';
import * as acl from 'common/utils/acl';

import { Block, Row } from 'emotion-jsxstyle';
import { EntityActions, EntityType } from 'common/entity';
import { getExportUrl, toApiError } from 'common/utils/api';

import { ActivityFeed } from 'common/components/activity/ActivityFeed';
import { AddBranchCountDialog } from './AddBranchCountDialog';
import { AppState } from 'common/models/AppState';
import { Badge } from 'common/components/Badge';
import { Card } from 'common/components/Card';
import { HistoryTabs } from 'common/components/HistoryTabs';
import { Loading } from 'common/components/Loading';
import { MediaImage } from 'common/components/MediaImage';
import { MemberDialog } from 'common/components/dialogs/MemberDialog';
import { MemberLink } from 'common/components/link/MemberLink';
import { OrgsBranches } from './OrgsBranches';
import { OrgsDetailInfo } from './OrgsDetailInfo';
import { OrgsEmployees } from './OrgsEmployees';
import { Page } from 'common/components/layout/Page/Page';
import { Link, RouteComponentProps } from 'react-router-dom';
import { TabPane } from 'reactstrap';
import { UserPermissions } from 'common/api/models/UserPermissions';
import { Member } from 'common/api/models/Member';
import { MemberApi } from 'common/api/resources/MemberApi';
import { OrderApi } from 'common/api/resources/OrderApi';
import { ConfirmDialog } from 'common/components/dialogs/ConfirmDialog';
import { MembersOrders } from '../Members/MembersOrders';
import { Order } from 'common/api/models/Order';
import { Analytics } from 'common/utils/Analytics';

export interface OrgsDetailProps {
  appState: AppState;
  entityActions: EntityActions;
  number: string;
  reloadAt: Date;
  onEdit: (org: Member) => void;
}
export interface OrgsDetailState {
  isLoading: boolean;
  error?: string;
  org?: Member;
  members: Member[];
  subOrgs: Member[];
  orders?: Order[];
  editMember?: Member;
  activeTab: string;
  addBranchCount: boolean;
  dialog?: string;
  dialogSuccess?: string;
  dialogError?: string;
  dialogLoading?: boolean;
}

export class OrgsDetail extends React.Component<OrgsDetailProps & RouteComponentProps<{}>, OrgsDetailState> {
  state: OrgsDetailState = { isLoading: false, members: [], subOrgs: [], activeTab: 'info', addBranchCount: false };

  componentDidMount() {
    this.reload(this.props.number);
  }

  componentDidUpdate(prevProps: OrgsDetailProps) {
    if (prevProps.number !== this.props.number || prevProps.reloadAt !== this.props.reloadAt) {
      this.reload(this.props.number);
    }
  }

  reload(num: string) {
    this.setState({ isLoading: true });
    MemberApi.getMemberByNumber(Number(num), { expandTags: true }).then(
      org => {
        this.props.entityActions.put(EntityType.MEMBER, org.id!, org);
        this.props.entityActions.fetch(EntityType.MEMBER, org.createId!);
        this.props.entityActions.fetch(EntityType.MEMBER, org.updateId!);
        this.props.entityActions.fetchIfPresent(EntityType.MEMBER, org.parentOrgId);
        if (org.contacts) {
          for (let c in org.contacts) {
            if (org.contacts.hasOwnProperty(c)) {
              this.props.entityActions.fetchIfPresent(EntityType.MEMBER, org.contacts[c]);
            }
          }
        }

        Promise.all([
          MemberApi.findMembers({ orgMemberId: org.id!, q: 'type:user' }),
          OrderApi.getOrders({ memberId: org.id }),
        ]).then(
          ([usersRes, ordersRes]) => {
            this.setState({ org, members: usersRes.data, orders: ordersRes.data, isLoading: false });
          },
          err => {
            this.setState({ error: toApiError(err) });
          }
        );
      },
      err => {
        this.setState({ error: toApiError(err) });
      }
    );
  }

  handleClickEdit = () => {
    this.props.onEdit(this.state.org!);
  };

  handleCreateMember = () => {
    this.setState({
      editMember: {
        name: this.state.org!.name,
        memberType: Member.MemberType.MEMB,
      },
    });
  };

  handleEditMember = () => {
    this.setState({
      editMember: this.state.org,
    });
  };

  handleSaveMember = () => {
    this.setState({ editMember: undefined });
    this.reload(this.props.number);
  };

  handleCloseDialog = () => {
    this.setState({
      dialog: undefined,
      dialogError: undefined,
      dialogSuccess: undefined,
      editMember: undefined,
    });
  };

  handleOpenActivate = () => {
    this.setState({ dialog: 'activate' });
  };

  speedBumpIfPendingOrders = (dialog: string, skip: () => void) => () => {
    const { orders = [] } = this.state;
    if (orders.filter(({ status }) => status === Order.Status.PENDING).length) {
      this.setState({ dialog });
    } else {
      skip();
    }
  };

  goToCreateOrder = () => {
    const { org } = this.state;
    if (org) {
      this.props.history.push(`/checkout?memberId=${org.id}`);
    }
  };

  goToPayDues = () => {
    const { org } = this.state;
    if (org) {
      this.props.history.push(`/checkout?memberId=${org.id}&renew=1`);
    }
  };

  handleActivate = () => {
    MemberApi.activateMember(this.state.org!.id!).then(
      res => {
        Analytics.member.update();
        this.setState({ dialog: undefined }, () => {
          this.reload(this.props.number);
        });
      },
      err => {
        this.setState({ dialogError: toApiError(err), dialogSuccess: undefined, dialogLoading: false });
      }
    );
  };

  handleUpdateTags = (tagIds: string[]) => {
    MemberApi.updateMember(this.state.org!.id!, { tagIds: tagIds }).then(
      res => {
        Analytics.member.update();
        this.reload(this.props.number);
      },
      err => {
        this.setState({ error: toApiError(err) });
      }
    );
  };

  handleUpdateInternalTags = (tagIds: string[]) => {
    MemberApi.updateMember(this.state.org!.id!, { internalTagIds: tagIds }).then(
      res => {
        Analytics.member.update();
        this.reload(this.props.number);
      },
      err => {
        this.setState({ error: toApiError(err) });
      }
    );
  };

  handleToggleAddBranchCount = () => {
    this.setState({ addBranchCount: !this.state.addBranchCount });
  };

  handleSubmitAddBranchCount = (count: number) => {
    this.props.history.push('/checkout?memberId=' + this.state.org!.id + '&addBranchCount=' + count);
  };

  handleGotoUpload = () => {
    const { org } = this.state;
    if (org) {
      this.props.history.push(`/uploadeligibility?memberId=${org.id}`);
    }
  };

  render() {
    const entities = this.props.appState.entities;
    const org = this.state.org!;

    if (this.state.error) {
      return <div className="alert alert-danger">{this.state.error}</div>;
    }
    if (this.state.isLoading || !org) {
      return <Loading />;
    }

    const member = org.id ? entities.member[org.id] : undefined;
    const parentOrg =
      org.parentOrgId && entities.member[org.parentOrgId] ? entities.member[org.parentOrgId] : undefined;
    const exportCsvUrl = getExportUrl('/v2/member/export', { q: 'employees:' + org.number });

    let tabs = [
      { name: 'info', label: 'Info' },
      { name: 'activity', label: 'Activity' },
      { name: 'orders', label: 'Orders', testId: 'orgsDetailTabOrders' },
      { name: 'employees', label: 'Employees' },
    ];
    if (!org.parentOrgId) {
      const totalBranchCount = org.branchCount! + (org.indBranchCount ? org.indBranchCount : 0);
      tabs = [
        ...tabs,
        {
          name: 'branches',
          label: 'Branches (' + totalBranchCount + ')',
        },
      ];
    }

    return (
      <Page
        title={org.name + ' (' + org.number + ')'}
        history={this.props.history}
        breadcrumbs={[{ label: 'Organizations', to: '/orgs?q=status:active' }]}
        primaryAction={{
          shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER),
          onClick: this.handleClickEdit,
          label: 'Edit Org',
        }}
        secondaryActions={[
          {
            shouldRender:
              acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER) &&
              !org.id &&
              !org.parentOrgId,
            label: 'Create Membership',
            iconName: 'id-card-o',
            onClick: this.handleCreateMember,
          },
          {
            shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER),
            label: 'Edit Membership',
            iconName: 'id-card-o',
            onClick: this.handleEditMember,
          },
          {
            shouldRender:
              org.status !== Member.Status.ACTIVE &&
              acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER),
            label: 'Reactivate',
            iconName: 'bolt',
            onClick: this.handleOpenActivate,
          },
          {
            shouldRender:
              acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_ORDER) && !org.parentOrgId,
            label: 'Pay Dues',
            iconName: 'usd',
            onClick: this.speedBumpIfPendingOrders('payDues', this.goToPayDues),
            testId: 'orgsDetailActionPayDues',
          },
          {
            shouldRender:
              acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_ORDER) && !org.parentOrgId,
            label: 'Purchase Branches',
            iconName: 'building-o',
            onClick: this.handleToggleAddBranchCount,
          },
          {
            shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_ORDER),
            label: 'Create Order',
            iconName: 'shopping-cart',
            onClick: this.speedBumpIfPendingOrders('createOrder', this.goToCreateOrder),
            testId: 'orgsDetailActionCreateOrder',
          },
          {
            shouldRender: acl.hasPermission(this.props.appState, UserPermissions.Permissions.EXPORT),
            href: exportCsvUrl,
            iconName: 'download',
            label: 'Export Employees CSV',
          },
          {
            shouldRender:
              acl.hasPermission(this.props.appState, UserPermissions.Permissions.IMPORT_ELIGIBILITY) &&
              !org.parentOrgId,
            iconName: 'upload',
            label: 'Upload Eligibility CSV',
            onClick: this.handleGotoUpload,
          },
        ]}
      >
        <Card padding="1rem" marginBottom="2rem">
          {parentOrg && (
            <Row>
              <Block>
                <b>
                  <MemberLink member={parentOrg} /> &gt;
                </b>
              </Block>
            </Row>
          )}
          <Row>
            <Block>
              <MediaImage
                path={org.imagePath}
                size="sm"
                className="rounded-circle"
                style={{ border: '1px solid #eee' }}
              />
            </Block>

            <Row justifyContent="space-around" alignItems="center" marginLeft=".5rem">
              <Block className="h4" marginRight=".5rem">
                {org.name}
              </Block>
              {org.status === Member.Status.ACTIVE && (
                <Badge type="success" text="Active" style={{ marginRight: '.5rem' }} />
              )}{' '}
              {org.memberType === Member.MemberType.IBRANCH && <Badge type="primary" text="Independent" />}
              {org.status === Member.Status.EXPIRED && (
                <Badge type="danger" text="Expired" style={{ marginRight: '.5rem' }} />
              )}{' '}
              {org.status === Member.Status.INACTIVE && (
                <Badge type="danger" text="Inactive" style={{ marginRight: '.5rem' }} />
              )}{' '}
              {org.status === Member.Status.REACTIVATING && (
                <Badge type="warning" text="Reactivating" style={{ marginRight: '.5rem' }} />
              )}{' '}
              {org.vars.ceuProvider && <Badge type="warning" text="CEU Provider" />}
            </Row>
          </Row>

          <HistoryTabs
            tabs={tabs}
            history={this.props.history}
            location={this.props.location}
            basePath={'/orgs/' + org.number}
          >
            <TabPane tabId="info">
              <OrgsDetailInfo
                org={org}
                member={member}
                subOrgs={this.state.subOrgs}
                appState={this.props.appState}
                onUpdateTags={this.handleUpdateTags}
                onUpdateInternalTags={this.handleUpdateInternalTags}
              />
            </TabPane>
            <TabPane tabId="activity">
              <ActivityFeed
                showTitle={false}
                appState={this.props.appState}
                entityActions={this.props.entityActions}
                entityId={this.state.org!.id!}
                entityType="ORG"
              />
            </TabPane>
            <TabPane tabId="orders">
              <MembersOrders
                appState={this.props.appState}
                member={member}
                entityActions={this.props.entityActions}
                onEdit={this.handleClickEdit}
              />
            </TabPane>
            <TabPane tabId="employees">
              <OrgsEmployees
                appState={this.props.appState}
                org={org}
                entityActions={this.props.entityActions}
                onEdit={this.handleClickEdit}
              />
            </TabPane>
            <TabPane tabId="branches">
              <OrgsBranches
                appState={this.props.appState}
                org={org}
                member={member}
                entityActions={this.props.entityActions}
                onEdit={this.handleClickEdit}
              />
            </TabPane>
          </HistoryTabs>
        </Card>
        <MemberDialog
          appState={this.props.appState}
          member={this.state.editMember}
          isOrg={true}
          onCancel={this.handleCloseDialog}
          onSave={this.handleSaveMember}
        />
        <AddBranchCountDialog
          isOpen={this.state.addBranchCount}
          onConfirm={this.handleSubmitAddBranchCount}
          onCancel={this.handleToggleAddBranchCount}
        />
        <ConfirmDialog
          title="Reactivate"
          message={'Are you sure you want to reactivate ' + org.name + '?'}
          isOpen={this.state.dialog === 'activate'}
          onConfirm={this.handleActivate}
          onCancel={this.handleCloseDialog}
          isLoading={this.state.dialogLoading}
          success={this.state.dialogSuccess}
          error={this.state.dialogError}
          loadingMessage="Activating..."
        />
        <ConfirmDialog
          title="Create Order"
          message={
            <span>
              Are you sure you want to create an order? This member has{' '}
              <Link onClick={this.handleCloseDialog} to={`/orgs/${org.number}/orders`}>
                pending orders
              </Link>.
            </span>
          }
          isOpen={this.state.dialog === 'createOrder'}
          onConfirm={this.goToCreateOrder}
          onCancel={this.handleCloseDialog}
        />
        <ConfirmDialog
          title="Pay Dues"
          message={
            <span>
              Are you sure you want to pay dues? This member has{' '}
              <Link onClick={this.handleCloseDialog} to={`/orgs/${org.number}/orders`}>
                pending orders
              </Link>.
            </span>
          }
          isOpen={this.state.dialog === 'payDues'}
          onConfirm={this.goToPayDues}
          onCancel={this.handleCloseDialog}
        />
      </Page>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Orgs/OrgsDetail.tsx